// Staff: who works at this site and in what role; invitations and removal.
import { useState } from "react";
import { Badge, Banner, Button, Checkbox, Dialog, DialogClose, Select, Table, TextField } from "@vavr/ui";
import { api, dateTime } from "../shared/index.ts";
import { useConsole, useGet, useAction } from "./data.tsx";
import { ConfirmButton, LoadError, Loading, ROLE_LABEL, Toolbar } from "./common.tsx";

interface Member { id: string; account_id: string; name: string; email: string; role: string; scope: string; room_ids: string[]; expires_at: number | null; active: boolean }

const ROLE_HELP: Record<string, string> = {
  site_admin: "Runs the site: roster, devices, staff, settings and reports. Must use a second factor.",
  door_staff: "Runs handovers at the door and sees the whole board.",
  teacher: "Sees and releases only the children in their rooms. Cannot export.",
  auditor: "Read-only access to records and reports for a licensing visit, for a few days.",
  "transport:dispatcher": "Plans routes, stops, riders and vehicles for the whole organization.",
  "transport:driver": "Runs the bus in the staff app: who gets on and off at each stop. Sees nothing else.",
};

export function Staff() {
  const { base, me, site } = useConsole();
  const members = useGet<{ members: Member[] }>(`${base}/members`);
  const rooms = useGet<{ rooms: { id: string; name: string }[] }>(`${base}/rooms`, 30_000);
  const act = useAction();
  if (members.loading) return <Loading />;
  if (members.error) return <LoadError error={members.error} retry={members.reload} />;
  const roomName = (id: string) => rooms.data?.rooms.find((r) => r.id === id)?.name ?? "";
  return (
    <div className="pmu-stack">
      <Toolbar actions={<InviteDialog rooms={rooms.data?.rooms ?? []} />} />
      {act.error && <Banner tone="danger" title={act.error} />}
      <Table
        caption="Staff at this site" hideCaption rowKey={(m) => m.id} rows={members.data?.members ?? []}
        columns={[
          { key: "name", header: "Name", rowHeader: true, render: (m) => <span className="pmu-stack pmu-c-tight"><span>{m.name}{m.account_id === me.account.id ? " (you)" : ""}</span><span className="pmu-small pmu-muted">{m.email}</span></span> },
          { key: "role", header: "Role", render: (m) => <Badge tone={m.role.endsWith("admin") ? "brand" : "neutral"}>{ROLE_LABEL[m.role] ?? m.role}</Badge> },
          { key: "scope", header: "Scope", render: (m) => (m.scope === "organisation" ? "All sites" : "This site") },
          { key: "rooms", header: "Rooms", render: (m) => (m.role === "teacher" ? m.room_ids.map(roomName).filter(Boolean).join(", ") || <Badge tone="warning">No room yet</Badge> : "") },
          { key: "exp", header: "Access", render: (m) => (!m.active ? <Badge>Ended</Badge> : m.expires_at ? `Until ${dateTime(m.expires_at, site.tz)}` : "Ongoing") },
          {
            key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end", render: (m) => m.account_id === me.account.id ? null : (
              <ConfirmButton size="sm" variant="ghost" danger title={`Remove ${m.name}?`} description={`${m.name} loses access to ${site.name} at once and their door PIN stops working. Records they made are kept.`} confirmLabel="Remove"
                onConfirm={() => act.run(() => api("DELETE", `${base}/members/${m.id}`), { success: `${m.name} removed.`, invalidate: [`${base}/members`] })}>Remove</ConfirmButton>
            ),
          },
        ]}
      />
    </div>
  );
}

function InviteDialog({ rooms }: { rooms: { id: string; name: string }[] }) {
  const { base, site, me } = useConsole();
  // A bus company's depot hires dispatchers and drivers; a school with Transport may too.
  const depot = site.org_kind === "operator";
  const transport = me.modules?.[site.org_id]?.includes("transport") ?? false;
  const roles = depot ? ["site_admin", "transport:dispatcher", "transport:driver"] : ["site_admin", "door_staff", "teacher", "auditor", ...(transport ? ["transport:dispatcher", "transport:driver"] : [])];
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", role: depot ? "transport:driver" : "door_staff", room_ids: [] as string[], days: "3" });
  const [sent, setSent] = useState<{ email: string; dev?: string } | null>(null);
  const act = useAction();
  const send = async () => {
    const r = await act.run(() => api("POST", `${base}/invitations`, {
      email: f.email, name: f.name || null, role: f.role, ...(f.role === "teacher" ? { room_ids: f.room_ids } : {}), ...(f.role === "auditor" ? { days: Number(f.days) } : {}),
    }), { invalidate: [`${base}/members`] });
    if (r) setSent({ email: f.email, dev: r.dev_token });
  };
  return (
    <Dialog title="Invite a staff member" open={open} onOpenChange={(o) => { setOpen(o); if (o) { setSent(null); setF({ ...f, email: "", name: "" }); } }} trigger={<Button icon="plus">Invite staff</Button>}
      actions={sent ? <DialogClose><Button>Done</Button></DialogClose> : <><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} disabled={!f.email.includes("@") || (f.role === "teacher" && f.room_ids.length === 0)} onClick={send}>Send invitation</Button></>}>
      {sent ? (
        <div className="pmu-stack">
          <Banner tone="success" title={`Invitation sent to ${sent.email}`}>The link works for 14 days. They choose a password when they accept.</Banner>
          {sent.dev && <p className="pmu-small pmu-muted">Development only: <a className="pmu-link" href={`/invite#${sent.dev}`}>accept link</a></p>}
        </div>
      ) : (
        <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); void send(); }}>
          {act.error && <Banner tone="danger" title={act.error} />}
          <div className="pmu-grid-2">
            <TextField label="Email" type="email" required autoFocus value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <TextField label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </div>
          <Select label="Role" value={f.role} onValueChange={(v) => setF({ ...f, role: v ?? "door_staff" })} hint={ROLE_HELP[f.role]}
            options={roles.map((r) => ({ value: r, label: ROLE_LABEL[r]! }))} />
          {f.role === "teacher" && (
            <fieldset className="pmu-c-fieldset">
              <legend>Rooms</legend>
              {rooms.length === 0 && <p className="pmu-muted">Add a room first, under Rooms.</p>}
              {rooms.map((r) => <Checkbox key={r.id} label={r.name} checked={f.room_ids.includes(r.id)} onCheckedChange={(c) => setF({ ...f, room_ids: c ? [...f.room_ids, r.id] : f.room_ids.filter((x) => x !== r.id) })} />)}
            </fieldset>
          )}
          {f.role === "auditor" && (
            <Select label="Access for" value={f.days} onValueChange={(v) => setF({ ...f, days: v ?? "3" })} options={[["1", "1 day"], ["3", "3 days"], ["7", "7 days"], ["14", "14 days"], ["30", "30 days"]].map(([value, label]) => ({ value: value!, label: label! }))} />
          )}
          <button type="submit" hidden />
        </form>
      )}
    </Dialog>
  );
}
